/**
 * The ONE implementation of every Event Outreach status action (#894), used by every screen that
 * offers it: the Attendees tab, the Outreach board (card, dropdown, drag, bulk bar), the Follow-up
 * board and Contact Detail's "Update status?" step.
 *
 * QA kept finding the same action recorded differently per screen (a linked activity and the chosen
 * channel from one, a fixed `event_outreach` channel and no activity from another), so a fix on one
 * screen left the next behind. Each action here records the same evidence wherever it starts:
 *
 * - **Stage change by hand** (Mark invited included): a completed note activity linked to the event
 *   carrying the chosen channel and note, then the #887 transition with that `activity_id` and
 *   `channel`, the follow-up date, the row's `state_version` and an operation id. Handed to sales goes
 *   through the hand-off route so the Lead Pipeline is written too.
 * - **Event email** (Invite / Send invite): the email is sent by the composer, then logged as a
 *   completed email activity on the event; the server moves the row to Invited and creates the one
 *   "Event invitation follow-up" task (`routes/activities.py`).
 * - **Bulk**: one operation id for the whole selection, so every contact's history entry shares it.
 * - **RSVP set by hand** (the Outreach board drawer): the same evidence activity and form as a stage
 *   change, then the #887 transition of `rsvp_status` only.
 * - **Final outcomes**: RSVP Declined / Cancelled, or any stage the workflow marks terminal (Closed,
 *   Handed to sales, Not interested by default), offer to close THAT event's open follow-ups.
 */
import axios from 'axios';
import { humanize } from './historyDisplay';
import { isTaskOpen } from './taskDue';
import { notifyTasksChanged } from './tasksApi';

const API = () => `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const enc = encodeURIComponent;

/** The channels a by-hand outreach action records (the server's `channel` vocabulary). */
export const OUTREACH_CHANNELS = [
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'in_person', label: 'In person' },
    { value: 'phone', label: 'Phone' },
    { value: 'email_outside_nurture', label: 'Email outside Nurture' },
];

export const INVITED_STAGE = 'invited';
export const HANDED_TO_SALES_STAGE = 'handed_to_sales';
export const FINAL_RSVP_STATUSES = ['declined', 'cancelled'];

/** The RSVP values a user can set by hand (the server's `rsvp_status` vocabulary). */
export const RSVP_OPTIONS = [
    { value: 'no_response', label: 'No response' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'declined', label: 'Declined' },
    { value: 'cancelled', label: 'Cancelled' },
];
export const rsvpLabel = value => RSVP_OPTIONS.find(option => option.value === value)?.label || 'No response';
export const CLOSE_FOLLOW_UPS_PROMPT = 'Close this event contact\'s open follow-ups?';

/** Event operation ids are URL-safe tokens server-side: never interpolate an email (`@`) raw. */
export const safeOperationId = (...parts) => parts.map(part => String(part ?? '').replace(/[^A-Za-z0-9_-]/g, '_')).join(':');

/** A fresh operation id for one user action; a bulk action uses ONE for its whole selection. */
export const newOperationId = (kind, eventId) => safeOperationId(kind, eventId, Date.now(), Math.random().toString(36).slice(2, 8));

const stageLabel = (stages, key) => (stages || []).find(stage => (stage.key || stage.stage_key) === key)?.label || String(key || '').replace(/[_-]+/g, ' ');
/** A channel's label: the by-hand list's own, else the humanized server value ('email' reads 'Email'). */
export const channelLabel = value => OUTREACH_CHANNELS.find(channel => channel.value === value)?.label || humanize(value);

/** True when this outcome ends the contact's outreach for the event (the prompt's one rule). */
export function isFinalOutcome({ stage, rsvpStatus, stages } = {}) {
    if (rsvpStatus && FINAL_RSVP_STATUSES.includes(rsvpStatus)) return true;
    if (!stage) return false;
    const definition = (stages || []).find(item => (item.key || item.stage_key) === stage);
    return Boolean(definition?.terminal);
}

/**
 * Complete this event's open follow-ups for the contact; another event's are never touched.
 * Studio send tasks (`outreach_send`) are not follow-ups: Mark sent closes them, and completing
 * one here would count as a message sent (#889). One `ncrm:tasks-changed` covers the whole batch.
 * `notify: false` leaves that notification to a caller closing several contacts' follow-ups at once.
 */
export async function closeEventFollowUps({ getAuthHeader, eventId, contactId, notify = true }) {
    const headers = getAuthHeader();
    const response = await axios.get(`${API()}/activities`, { params: { contact_id: contactId, event_id: eventId }, headers });
    const rows = Array.isArray(response.data) ? response.data : (response.data?.data || []);
    // The server filters on `event_id`; the second check only guards a row that says otherwise.
    const open = rows.filter(row => row.type === 'task' && row.task_kind !== 'outreach_send'
        && (!row.event_id || row.event_id === eventId) && isTaskOpen(row));
    if (!open.length) return 0;
    try {
        await Promise.all(open.map(row => axios.put(`${API()}/activities/${enc(row.activity_id)}/complete`, {}, { headers })));
    } finally {
        if (notify) notifyTasksChanged();
    }
    return open.length;
}

/**
 * After a final outcome, ask once whether to close that event's open follow-ups for the contacts
 * (`contactIds`), and close them on yes. Answers `{ asked, closed, failed }`. The whole selection
 * notifies once, not once per contact (each notification refetches the Tasks page and the badge).
 */
export async function offerToCloseFollowUps({ getAuthHeader, eventId, contactIds, confirm = window.confirm }) {
    const ids = (contactIds || []).filter(Boolean);
    if (!ids.length || !confirm(CLOSE_FOLLOW_UPS_PROMPT)) return { asked: ids.length > 0, closed: 0, failed: 0 };
    const results = await Promise.allSettled(ids.map(contactId => closeEventFollowUps({ getAuthHeader, eventId, contactId, notify: false })));
    // A contact whose close failed may have completed some tasks first, so a failure notifies too; a batch that
    // found nothing open and failed nowhere changed nothing and notifies nothing.
    if (results.some(result => result.status === 'rejected' || result.value > 0)) notifyTasksChanged();
    return {
        asked: true,
        closed: results.filter(result => result.status === 'fulfilled').reduce((sum, result) => sum + result.value, 0),
        failed: results.filter(result => result.status === 'rejected').length,
    };
}

/**
 * Record an email that was sent to an event contact: a completed email activity on the event. The
 * server moves a To invite contact to Invited and creates the one invitation follow-up task.
 * `operationId` is shared by a bulk Send invite. Answers the activity.
 */
export async function logEventEmail({ getAuthHeader, eventId, contactId, subject, operationId }) {
    const response = await axios.post(`${API()}/activities`, {
        type: 'email',
        title: subject?.trim() || 'Event invitation',
        description: subject?.trim() ? `Email sent: ${subject.trim()}` : 'Event invitation sent',
        contact_id: contactId, event_id: eventId, status: 'completed',
        completed_at: new Date().toISOString(), channel: 'email', source: 'manual',
        ...(operationId ? { event_operation_id: operationId } : {}),
    }, { headers: getAuthHeader() });
    return response.data;
}

/** The column a hand-off lands in since #1013 (`handed_to_sales` before it). */
export const CONVERTED_STAGE = 'converted';
const HANDOFF_TARGETS = [CONVERTED_STAGE, HANDED_TO_SALES_STAGE];
/** Targets a move records as FACTS through the PATCH route's `journey_target` (#1013). */
const FACT_JOURNEY_TARGETS = ['to_invite', 'invited', 'going', 'attended', 'no_show', 'closed'];

/**
 * A by-hand move on the Guests tab (#1013): the evidence a by-hand move leaves (a linked activity
 * with the chosen channel and note), then ONE transition. A milestone target (To invite, Invited, Going,
 * Attended, No-show, Closed) is a fact the server derives the column from, so it goes as `journey_target`
 * (+ `close_reason` for Closed); a follow-up stage key goes as `outreach_stage`; Converted goes through the
 * hand-off route so the Lead Pipeline is written too. A stale `state_version` is the server's 409
 * `event_contact_conflict`, surfaced unchanged. Answers the new row.
 */
export async function recordJourneyMove({
    getAuthHeader, eventId, contact, target, label, closeReason, channel, note = '', followUpDate = '', operationId,
}) {
    if (!channel) throw new Error('Choose how you reached this contact.');
    const headers = getAuthHeader();
    const contactId = contact.contact_id;
    const title = label || String(target || '').replace(/[_-]+/g, ' ');
    const invite = target === INVITED_STAGE;
    const activity = await axios.post(`${API()}/activities`, {
        type: 'note',
        title: invite ? 'Marked invited' : `Moved to ${title}`,
        description: note || `${invite ? 'Invited' : `Moved to ${title}`} via ${channelLabel(channel)}`,
        contact_id: contactId, event_id: eventId, status: 'completed', channel, source: 'manual',
    }, { headers });
    const activityId = activity.data?.activity_id;
    const operation = operationId || safeOperationId('journey', eventId, contactId, target, contact.state_version ?? 0);
    if (HANDOFF_TARGETS.includes(target)) {
        const handoff = await axios.post(`${API()}/events/${enc(eventId)}/contacts/${enc(contactId)}/handoff`, {
            expected_state_version: contact.state_version ?? 0, activity_id: activityId, channel,
            reason: note || 'Handed to sales', operation_id: operation,
        }, { headers });
        return handoff.data?.attendee;
    }
    const fact = FACT_JOURNEY_TARGETS.includes(target);
    const transition = await axios.patch(`${API()}/events/${enc(eventId)}/contacts/${enc(contactId)}`, {
        ...(fact ? { journey_target: target, close_reason: target === 'closed' ? (closeReason || 'closed') : undefined } : { outreach_stage: target }),
        activity_id: activityId, channel,
        reason: invite ? 'mark_invited' : 'journey_move',
        notes: note || undefined,
        next_follow_up_at: followUpDate ? `${followUpDate}T23:59:59Z` : undefined,
        expected_state_version: contact.state_version ?? 0,
        operation_id: operation,
    }, { headers });
    return transition.data?.attendee;
}

/** The same move for a selection, sharing one operation id; per-contact results, in order. */
export async function recordBulkJourneyMove({ contacts, operationId, ...rest }) {
    const operation = operationId || newOperationId(`bulk-${rest.target}`, rest.eventId);
    const results = [];
    // One at a time: each is a full audited transition server-side (CAS, history, journal).
    for (const contact of contacts) {
        try {
            results.push({ status: 'fulfilled', contact, value: await recordJourneyMove({ ...rest, contact, operationId: operation }) });
        } catch (reason) {
            results.push({ status: 'rejected', contact, reason });
        }
    }
    return { operationId: operation, results };
}
